import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  appliedTheme: 'light' | 'dark'; // The actual theme being used (resolved system preference)
}

interface ThemeActions {
  setTheme: (theme: Theme) => void;
  setAppliedTheme: (appliedTheme: 'light' | 'dark') => void;
}

type ThemeStore = ThemeState & ThemeActions;

const initialState: ThemeState = {
  theme: 'light',
  appliedTheme: 'light',
};

export const useThemeStore = create<ThemeStore>()(
  devtools(
    persist(
      set => ({
        ...initialState,

        setTheme: theme => set({ theme }, false, 'setTheme'),

        setAppliedTheme: appliedTheme =>
          set({ appliedTheme }, false, 'setAppliedTheme'),
      }),
      {
        name: 'theme-store',
        version: 1,
        partialize: (state: ThemeStore) => ({
          theme: state.theme,
          // Don't persist appliedTheme as it should be calculated on load
        }),
      }
    ),
    {
      name: 'ThemeStore',
    }
  )
);

// Selectors
export const useTheme = () => useThemeStore(state => state.theme);

export const useAppliedTheme = () => useThemeStore(state => state.appliedTheme);

// Composite selectors with shallow comparison
export const useThemeState = () =>
  useThemeStore(
    state => ({
      theme: state.theme,
      appliedTheme: state.appliedTheme,
    }),
    shallow
  );

// Action selectors for performance
export const useThemeActions = () =>
  useThemeStore(
    state => ({
      setTheme: state.setTheme,
      setAppliedTheme: state.setAppliedTheme,
    }),
    shallow
  );
